import React from 'react';
import { BriefcaseIcon, CarIcon, BuildingIcon, HeartPulseIcon, HelpCircleIcon, PhoneIcon, ShieldCheckIcon, ClipboardListIcon, FileSearchIcon, MessagesSquareIcon, ScaleIcon, MapPinIcon, StarIcon, ClockIcon, UsersIcon, CheckCircle2Icon, HeartHandshakeIcon, BoxIcon } from 'lucide-react';
// Firm-wide constants — single source of truth, kept literal for Bricks portability.
// Verified from separoviclawyers.com, the ABR (ABN registry) and LinkedIn.
export const PHONE_DISPLAY = '08 9227 1000';
export const PHONE_TEL = 'tel:+61892271000';
export const EMAIL = 'enquiries@separoviclawyers.com';
export const EMAIL_HREF = 'mailto:enquiries@separoviclawyers.com';
export const ADDRESS = '453 Beaufort Street, Highgate WA 6003';
export const HOURS = 'Mon–Fri, 8:30am–5pm';
export const ABN = '63 463 286 531';
export const EST_YEAR = 1990;
export const HIGHGATE_SINCE = 1993;
export const LEGAL_NAME = 'Separovic Injury Lawyers';
// Lucide outline iconMap — import only what is used across sections
export const iconMap: Record<string, BoxIcon> = {
  work: BriefcaseIcon,
  road: CarIcon,
  public: BuildingIcon,
  serious: HeartPulseIcon,
  family: HeartHandshakeIcon,
  unsure: HelpCircleIcon,
  phone: PhoneIcon,
  shield: ShieldCheckIcon,
  clipboard: ClipboardListIcon,
  review: FileSearchIcon,
  explain: MessagesSquareIcon,
  scale: ScaleIcon,
  location: MapPinIcon,
  star: StarIcon,
  clock: ClockIcon,
  team: UsersIcon,
  check: CheckCircle2Icon
};
interface SectionProps {
  id?: string;
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div';
}
// Compact but breathable section rhythm. Tighter than the original system,
// without collapsing the visual boundary between persuasion stages.
export function Section({
  id,
  children,
  className = '',
  as = 'section'
}: SectionProps) {
  const Tag = as;
  return <Tag id={id} className={`w-full py-10 md:py-12 lg:py-14 ${className}`} data-id="element-330" data-mp="12xf9i3ifj7zm">
      <div className="mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8" data-id="element-331" data-mp="12y8h16p7q1it">
        {children}
      </div>
    </Tag>;
}
// The signature "guidance line" motif — CSS-only decorative rule
export function GuidanceLine({
  className = ''
}: {
  className?: string;
}) {
  return <span aria-hidden="true" className={`guidance-line ${className}`} data-id="element-332" data-mp="jcou1pm1tblz" />;
}
interface CTAButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
  ariaLabel?: string;
}
export function CTAButton({
  href,
  children,
  variant = 'primary',
  className = '',
  ariaLabel
}: CTAButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 min-h-[50px] px-6 rounded-btn text-body font-semibold transition-[transform,background-color,border-color,color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2';
  const variants = {
    // Primary = conversion accent so CTAs pop off the page
    primary: 'bg-signal text-paper shadow-[0_6px_20px_-8px_rgba(188,63,32,0.7)] hover:bg-signal-600 hover:-translate-y-0.5',
    // Secondary = quiet outline on light surfaces
    secondary: 'bg-transparent text-forest border border-forest/25 hover:border-forest hover:bg-forest/5',
    // Ghost = solid forest, used on light surfaces where a calm primary is wanted
    ghost: 'bg-forest text-paper hover:bg-forest-700 hover:-translate-y-0.5'
  };
  return <a href={href} aria-label={ariaLabel} className={`${base} ${variants[variant]} ${className}`} data-id="element-333" data-mp="1q6ct3kcocpqj">
      {children}
    </a>;
}
// Closing CTA panel — the forest reassurance block that used to be a standalone
// mid-page section. Now composed inside sections that need a closing ask.
export function ClosingCTAPanel({
  title = 'You Do Not Need Every Answer Before You Call',
  body = 'Start with a confidential conversation about what happened and what you are worried about.',
  className = ''
}: {
  title?: string;
  body?: string;
  className?: string;
}) {
  return <div className={`rounded-frame bg-forest px-6 py-9 text-center shadow-card-lg md:px-12 md:py-10 ${className}`} data-id="element-334" data-mp="1g4k060v72i8y">
      <div className="mx-auto flex justify-center" data-id="element-335" data-mp="1l3wwdc7yh1o7">
        <GuidanceLine data-id="element-336" data-mp="1q03qm7qzooqb" />
      </div>
      <h3 className="mx-auto mt-5 max-w-2xl font-serif text-[30px] font-medium leading-[1.14] tracking-[-0.015em] text-paper md:text-[36px]" data-id="element-337" data-mp="1q39i1tn3c2p7">
        {title}
      </h3>
      <p className="mx-auto mt-4 max-w-xl text-body-lg text-paper/75" data-id="element-338" data-mp="1z11xqywq5cd4">{body}</p>
      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row" data-id="element-339" data-mp="24065dl40321c">
        <CTAButton href="/#compensation-calculator" className="px-7" data-id="element-340" data-mp="28rwmqb1cuq29">
          Start free claim check
        </CTAButton>
        <a href={PHONE_TEL} className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-btn border border-paper/25 px-6 text-body font-semibold text-paper transition-colors duration-200 ease-out hover:bg-paper/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-341" data-mp="2dr0uqycjmhr0">
          <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-342" data-mp="b5dm3t6zhn2w" />
          Call {PHONE_DISPLAY}
        </a>
      </div>
      <p className="mt-5 text-small text-paper/55" data-id="element-343" data-mp="23e6n5bmar22">
        No long questionnaire. No obligation to proceed.
      </p>
    </div>;
}
// Small proof/label pill — fully rounded only for small labels per shape system
export function Pill({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-paper px-3 py-1 text-small font-medium text-slate" data-id="element-344" data-mp="psyo1js4hg98">
      {children}
    </span>;
}
export function SectionEyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-3" data-id="element-345" data-mp="g2ni5rovowzp">
      <GuidanceLine data-id="element-346" data-mp="1diay0lv9xscl" />
      <span className="text-small font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-347" data-mp="1djmb5cam0cgd">
        {children}
      </span>
    </div>;
}
// Standardised section header: eyebrow -> H2 -> optional intro, with one
// consistent vertical rhythm and type scale across every section. `tone`
// swaps colours for use on dark (forest) bands. `align` centres the block.
interface SectionHeadingProps {
  eyebrow: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  tone?: 'light' | 'dark';
  align?: 'left' | 'center';
  className?: string;
}
export function SectionHeading({
  eyebrow,
  title,
  intro,
  tone = 'light',
  align = 'left',
  className = ''
}: SectionHeadingProps) {
  const isDark = tone === 'dark';
  const centered = align === 'center';
  return <div className={`${centered ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'} ${className}`} data-id="element-348" data-mp="zwwz2s4vjati">
      <div className={centered ? 'flex justify-center' : ''} data-id="element-349" data-mp="13y8qn37b664c">
        <SectionEyebrow data-id="element-350" data-mp="13ys9qn8p30jy">{eyebrow}</SectionEyebrow>
      </div>
      <h2 className={`mt-4 font-serif text-[30px] font-medium leading-[1.12] tracking-[-0.015em] md:text-h2 ${isDark ? 'text-paper' : 'text-forest'}`} data-id="element-351" data-mp="2uaods6228kvt">
        {title}
      </h2>
      {intro && <p className={`mt-3 max-w-[68ch] text-body ${isDark ? 'text-paper/75' : 'text-slate'}`} data-id="element-352" data-mp="mpzgpm4nj3yz">
          {intro}
        </p>}
    </div>;
}